import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Paper, Alert } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AccountService from '../../Services/http/AccountService';
import { TAccountsOverviewResponse } from '../../types/AccountOverview.type';
import { setIsActiveSpinner } from '../../Redux/GeneralSlice';
import AccountsOverviewTable from './AccountsOverviewTable';
import './AccountsOverview.scss';
import { useTranslation } from 'react-i18next';

const AccountsOverview: React.FC = () => {
  const { t } = useTranslation("accountsOverview");

  const [data, setData] = useState<TAccountsOverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAccountsOverview(page + 1, rowsPerPage);
  }, [page, rowsPerPage]);

  const fetchAccountsOverview = async (pageNum: number, pageSize: number) => {
    try {
      dispatch(setIsActiveSpinner(true));
      setIsLoading(true);
      setError(null);

      const response = await AccountService.getAccountsOverview(pageNum, pageSize);

      if (!response) {
        setError("errors.fetchFailed");
        setData(null);
        return;
      }

      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.generic"));
      setData(null);
    } finally {
      setIsLoading(false);
      dispatch(setIsActiveSpinner(false));
    }
  };

  const handleRowClick = (accountId: number) => {
    navigate(`/layout/dashboard/${accountId}`);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="lg" className="accounts-overview">
      <Box className="accounts-overview__header">
        <Typography variant="h4" component="h1" className="accounts-overview__title">
          {t("title")}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" className="accounts-overview__error">
          {t(error)}
        </Alert>
      )}

      {isLoading ? (
        <Box className="accounts-overview__loading">
          <Typography>{t("loading")}</Typography>
        </Box>
      ) : !data ? (
        null
      ) : data.data.length === 0 ? (
        <Box className="accounts-overview__empty">
          <Alert severity="info">{t("empty")}</Alert>
        </Box>
      ) : (
        <Paper className="accounts-overview__paper">
          <AccountsOverviewTable
            accounts={data.data}
            page={page}
            rowsPerPage={rowsPerPage}
            totalAccounts={data.total}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onRowClick={handleRowClick}
          />
        </Paper>
      )}
    </Container>
  );
};

export default AccountsOverview;
